import { useTranslation } from "react-i18next"
import ChartPlaceholder from "../../../../components/placeholder/components/ChartPlaceholder"
import { Spacer } from "../../../../components/spacer/Spacer"
import Text from "../../../../components/Text"
import { getCurrency } from "../../../../i18n/config"
import { styled, theme } from "../../../../styles/stitches.config"
import { CalculationLabel } from "../CalculationLabel"
import ChartLegend from "../../../../components/chart/ChartLegend"
import { PageContainer, ConfigContainer } from "../containers"
import { PowerAndProductionInput } from "../input/PowerAndProductionInput"
import { ProductionChartResponsive } from "./ProductionChart"
import { FormattedUnit } from "../../../../components/FormattedUnit"
import { useIsDesktopVersion } from "../../../../hooks/mediaHooks"
import { CHART_X_PADDING } from "../../../../components/chart/hooks"
import { useDailyProductionController } from "./useProductionController"
import { ChartLegendContainer } from "../ChartLegendContainer"

const SCard = styled("div", {
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.default,
  padding: `${theme.space.s7} ${theme.space.s12}`,

  display: "flex",
  flexDirection: "column",
  gap: theme.space.s1,
  justifyContent: "center",
  alignItems: "center",

  "@sm": {
    alignItems: "flex-end",
  },
})

const FooterContainer = styled("div", {
  paddingBottom: theme.space.s6,

  display: "flex",
  flexDirection: "column",
  justifyContent: "unset",
  alignItems: "center",

  "@sm": {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "unset",
    paddingInline: CHART_X_PADDING,
  },
})

const HeaderContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "none",
  alignItems: "center",

  "@sm": {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingInline: CHART_X_PADDING,
  },
})

const Summary = ({ total }: { total: number }) => {
  const { t } = useTranslation(["microsite"])
  return (
    <SCard>
      <Text type="textsLarge">{t("microsite:your_production")}</Text>
      <FormattedUnit
        value={total}
        valueTextType="headerH2Neutral"
        unit={getCurrency().symbol}
        unitTextType="headerH5Neutral"
        maxFractionDigits={0}
      />
    </SCard>
  )
}

export const Production = () => {
  const isDesktop = useIsDesktopVersion()
  const controller = useDailyProductionController()

  return (
    <PageContainer>
      <HeaderContainer>
        <ConfigContainer>
          <PowerAndProductionInput
            installedPowerInKWp={controller.installedPower}
            onChange={controller.setInstalledPower}
          />
          <Spacer size={theme.space.s6} />

          <CalculationLabel />
        </ConfigContainer>
      </HeaderContainer>

      <Spacer size={theme.space.s4} />

      {controller.isLoading ? (
        <ChartPlaceholder />
      ) : (
        <>
          <ProductionChartResponsive
            isErrorState={controller.isError}
            data={controller.chartData}
            filter={controller.filter}
          />

          <ChartLegendContainer>
            <ChartLegend items={controller.legendItems} />
          </ChartLegendContainer>

          <Spacer size={theme.space.s10} />

          <FooterContainer>
            {isDesktop && <Spacer size={theme.space.s12} />}

            <Summary total={controller.total} />
          </FooterContainer>
        </>
      )}
    </PageContainer>
  )
}
